import { useEffect } from "react";
import { router } from "@inertiajs/react";
import { toast } from "sonner";

/**
 * FlashMessages component
 *
 * Automatically displays flash messages from the backend as toast notifications.
 * This component listens to Inertia navigation events and displays flash messages.
 *
 * Supported flash message types:
 * - notice: Info/success messages
 * - alert: Warning/error messages
 * - success: Success messages
 * - error: Error messages
 */
export function FlashMessages() {
  useEffect(() => {
    // Track if we've already shown messages to prevent duplicates
    let lastFlashShown: string | null = null;

    // Listen for successful page loads
    const removeListener = router.on("success", (event) => {
      const flash = event.detail.page.props.flash as
        | {
            notice?: string;
            alert?: string;
            success?: string;
            error?: string;
          }
        | undefined;

      if (!flash) return;

      // Create a unique key from flash messages to prevent showing same message twice
      const flashKey = JSON.stringify(flash);
      if (flashKey === lastFlashShown) return;
      lastFlashShown = flashKey;

      // Display success/notice messages (only one)
      if (flash.success) {
        toast.success(flash.success);
      } else if (flash.notice) {
        toast.success(flash.notice);
      }

      // Display error/alert messages (only one)
      if (flash.error) {
        toast.error(flash.error);
      } else if (flash.alert) {
        toast.error(flash.alert);
      }
    });

    // Cleanup listener on unmount
    return () => removeListener();
  }, []);

  // This component doesn't render anything visible
  return null;
}
